import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { CartingConfigTransferService } from '@shared/services/export-import-services/carting-config';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { CartingConfigService } from './services/carting.config.service';

@Component({
	selector: 'app-carting-config',
	templateUrl: './carting.config.component.html',
	styleUrls: ['./carting.config.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CartingConfigComponent implements OnInit, OnDestroy {
	private unsubscribe = new Subject<void>();

	public favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_reset',
			text: () => 'masterdata.common.resetAllChanges',
			click: () => this.resetConfiguration(),
			disabled: () => this.service.viewModel.isResetButtonDisabled,
		},
		{
			icon: () => 'acies_save',
			text: () => 'masterdata.common.saveAllChanges',
			click: () => this.saveConfiguration(),
			disabled: () => this.service.viewModel.isSaveButtonDisabled,
		},
	];

	public menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.carting.configImportExport.export.button',
			click: () => this.exportConfiguration(),
			permissions: () => ['carting:ConfigList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.carting.configImportExport.import.button',
			click: () => this.onImportConfiguration(),
			permissions: () => ['carting:ConfigPut', 'carting:ConfigList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.carting.configImportExport.import.template.button',
			click: () => this.downloadTemplate(),
			permissions: () => ['carting:ConfigList'],
		},
	];

	constructor(
		public service: CartingConfigService,
		private dialog: MatDialog,
		private notificationService: NotificationService,
		private cartingConfigTransferService: CartingConfigTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	public ngOnInit(): void {
		this.service.loadConfiguration();
	}

	public ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	public updatePriorityFormValidity(): void {
		this.service.updatePriorityFormValidity();
	}

	public updateConfiguration(): void {
		this.service.updateConfiguration();
	}

	public saveConfiguration(): void {
		this.service.saveConfiguration();
	}

	public resetConfiguration(): void {
		this.service.loadConfiguration();
	}

	public downloadTemplate(): void {
		this.cartingConfigTransferService.downloadTemplate();
	}

	public exportConfiguration(): void {
		const config = this.service.serverConfiguration;

		this.cartingConfigTransferService
			.exportData(config ? [config] : [])
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.carting.configImportExport.export.notification.success.header'], ['masterdata.carting.configImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export carting configuration', error);
					this.notificationService.notify(['masterdata.carting.configImportExport.export.notification.error.header'], ['masterdata.carting.configImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImportConfiguration(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.carting.configImportExport.import.title',
				subHeadingKey: 'masterdata.carting.configImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.carting.configImportExport.import.button',
				notificationHeader: 'masterdata.carting.configImportExport.import.notification.success.header',
				notificationContent: 'masterdata.carting.configImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.carting.configImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.carting.configImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.carting.configImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.carting.configImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.cartingConfigTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.service.loadConfiguration();
			});
	}
}
